import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getTimePermissionModuleCapabilities } from '../../config/timePermissionCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useWorkflowInbox } from '../../hooks/useWorkflowInbox';
import type { AdminTimePermissionStatusTab } from '../../types/timePermission';
import type { WorkflowInboxItem } from '../../types/workflow';
import { permissionApprovalListTitle } from '../../utils/approvalRoles';
import { formatAppliedOn } from '../../utils/dateTime';
import { isRejectedWorkflowStatus } from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'AdminTimePermissionRequests'
>;

const STATUS_TABS: readonly AdminTimePermissionStatusTab[] = [
  'Pending',
  'Approved',
  'Rejected',
  'All',
];

function matchesStatusTab(
  item: WorkflowInboxItem,
  tab: AdminTimePermissionStatusTab,
): boolean {
  if (tab === 'All') return true;
  const code = (item.workflowStatusCode || '').toUpperCase();
  const label = item.workflowStatusLabel.toLowerCase();
  if (tab === 'Pending') {
    return (
      code === 'PENDING' ||
      code === 'IN_PROGRESS' ||
      label.includes('pending') ||
      label.includes('progress')
    );
  }
  if (tab === 'Approved') {
    return (
      code === 'APPROVED' || code === 'COMPLETED' || label.includes('approved')
    );
  }
  return code === 'REJECTED' || label.includes('rejected');
}

function isPermissionRequest(item: WorkflowInboxItem): boolean {
  const code = (item.requestTypeCode || '').toUpperCase();
  return code === 'PERMISSION_REQUEST' || code === 'PERMISSION';
}

function timeRangeLabel(item: WorkflowInboxItem): string {
  if (item.requestSummary?.trim()) return item.requestSummary.trim();
  return 'Time range unavailable';
}

function dateLabel(item: WorkflowInboxItem): string {
  const appliedOn = formatAppliedOn(item.submittedAt);
  if (appliedOn !== 'Not available') return appliedOn;
  return 'Date unavailable';
}

function PermissionRequestRow({
  item,
  onPress,
  palette,
}: {
  item: WorkflowInboxItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const employeeName =
    item.employee.displayName ||
    item.employee.employeeNumber ||
    'Employee unavailable';

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${employeeName}, ${timeRangeLabel(item)}, ${
        item.workflowStatusLabel
      }`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowMain}>
        <Text style={[styles.employeeName, { color: palette.textPrimary }]}>
          {employeeName}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {dateLabel(item)}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {timeRangeLabel(item)}
        </Text>
        {isRejectedWorkflowStatus(item.workflowStatusCode) &&
        item.rejectReason?.trim() ? (
          <Text
            style={[styles.meta, { color: palette.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
            {...(Platform.OS === 'web'
              ? { title: item.rejectReason.trim() }
              : null)}
          >
            Reject Reason: {item.rejectReason.trim()}
          </Text>
        ) : null}
      </View>
      <LeaveStatusBadge
        label={item.workflowStatusLabel}
        statusCode={item.workflowStatusCode}
      />
    </Pressable>
  );
}

function StatusTabs({
  value,
  counts,
  onChange,
  palette,
}: {
  value: AdminTimePermissionStatusTab;
  counts: Record<AdminTimePermissionStatusTab, number>;
  onChange: (tab: AdminTimePermissionStatusTab) => void;
  palette: ColorPalette;
}) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.tabsContent}
      style={styles.tabsScroll}
    >
      {STATUS_TABS.map(tab => {
        const selected = tab === value;
        const count = counts[tab];
        const label =
          tab === 'Pending' && count > 0 ? `${tab} (${count})` : tab;
        return (
          <Pressable
            key={tab}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={label}
            onPress={() => onChange(tab)}
            style={[
              styles.tab,
              {
                backgroundColor: selected
                  ? `${palette.info}18`
                  : palette.surface,
                borderColor: selected ? palette.info : palette.border,
              },
            ]}
          >
            <Text
              style={[
                styles.tabLabel,
                { color: selected ? palette.info : palette.textPrimary },
              ]}
            >
              {label}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

export function AdminTimePermissionRequestListScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getTimePermissionModuleCapabilities(auth.permissions);
  const [statusTab, setStatusTab] =
    useState<AdminTimePermissionStatusTab>('Pending');
  const inbox = useWorkflowInbox();
  const {
    applyFilters,
    refreshInbox,
    retry,
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    loadMore,
  } = inbox;

  useFocusEffect(
    useCallback(() => {
      if (!capabilities.canViewAdminRequests) return;
      applyFilters({
        entityType: 'PERMISSION_REQUEST',
        status:
          statusTab === 'All'
            ? 'ALL'
            : (statusTab.toUpperCase() as 'PENDING' | 'APPROVED' | 'REJECTED'),
      });
    }, [applyFilters, capabilities.canViewAdminRequests, statusTab]),
  );

  const permissionItems = useMemo(
    () => items.filter(isPermissionRequest),
    [items],
  );

  const counts = useMemo(() => {
    const next: Record<AdminTimePermissionStatusTab, number> = {
      Pending: 0,
      Approved: 0,
      Rejected: 0,
      All: permissionItems.length,
    };
    for (const item of permissionItems) {
      if (matchesStatusTab(item, 'Pending')) next.Pending += 1;
      if (matchesStatusTab(item, 'Approved')) next.Approved += 1;
      if (matchesStatusTab(item, 'Rejected')) next.Rejected += 1;
    }
    return next;
  }, [permissionItems]);

  const visibleItems = useMemo(
    () => permissionItems.filter(item => matchesStatusTab(item, statusTab)),
    [permissionItems, statusTab],
  );

  const footer = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View
        style={styles.footer}
        accessibilityLabel="Loading more permission requests"
      >
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [isLoadingMore, palette.primary]);

  const emptyMessage =
    statusTab === 'Pending'
      ? 'There are no pending permission requests waiting for approval.'
      : `No ${statusTab.toLowerCase()} permission requests were found.`;

  return (
    <PermissionGuard
      permission={PERMISSIONS.PERMISSION_APPROVE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader title={permissionApprovalListTitle(auth.roles)} onBack={navigation.goBack} />
        <View style={styles.body}>
          <StatusTabs
            value={statusTab}
            counts={counts}
            onChange={setStatusTab}
            palette={palette}
          />

          {isLoading && permissionItems.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator
                color={palette.primary}
                accessibilityLabel="Loading permission requests"
              />
            </View>
          ) : error && permissionItems.length === 0 ? (
            <ErrorState message={error} onRetry={retry} />
          ) : (
            <FlatList
              data={visibleItems}
              keyExtractor={item => String(item.workflowInstanceId)}
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={refreshInbox}
                  tintColor={palette.primary}
                />
              }
              onEndReached={loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title="No permission requests"
                  message={emptyMessage}
                  actionTitle="Retry"
                  onAction={retry}
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <PermissionRequestRow
                  item={item}
                  palette={palette}
                  onPress={() =>
                    navigation.navigate(routes.timePermissionApproval, {
                      workflowInstanceId: item.workflowInstanceId,
                    })
                  }
                />
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.md },
  tabsScroll: { flexGrow: 0, marginBottom: spacing.md },
  tabsContent: {
    gap: spacing.sm,
    paddingRight: spacing.md,
  },
  tab: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  tabLabel: {
    ...typography.caption,
    fontWeight: '700',
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowMain: { flex: 1, gap: spacing.xs },
  employeeName: { ...typography.body, fontWeight: '700' },
  meta: { ...typography.caption },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
